import { ScanLine, CheckCircle2, ArrowUpRight } from "lucide-react";

export default function ProductSection() {
  return (
    <section
      id="product"
      data-testid="product-section"
      className="relative border-b border-line bg-white text-ink border border-line"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="flex items-center justify-between mb-14">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            02 / The Product
          </span>
          <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft">
            Scan time · &lt; 2.0s
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <h2
              data-testid="product-headline"
              className="font-display font-bold tracking-tight text-ink text-[1.75rem] sm:text-[2rem] lg:text-[2.25rem] leading-[1.2]"
            >
              A barcode. A URL.{" "}
              <span className="text-brand-orange">A verdict.</span> An
              alternative.
            </h2>

            <div className="mt-10 space-y-6 text-lg sm:text-xl text-ink-soft leading-relaxed max-w-2xl">
              <p>
                SheScan is a live, multi-platform ecosystem that tells you
                instantly whether the product in your hand or the service
                provider on your screen is women-owned — and surfaces a
                verified women-owned alternative when it isn't.
              </p>
              <p className="text-ink">
                Every scan or click is a data point. Every redirected dollar is
                tracked. Every user is building a cumulative impact record that
                shows exactly where her money actually went.
              </p>
              <p>
                The mechanic is simple. The infrastructure underneath it is
                not. SheScan runs on a proprietary{" "}
                <span className="text-brand-orange font-mono">
                  Gender-Identity Metadata
                </span>{" "}
                architecture — a live verification system that distinguishes
                true ownership from branding, so what the app and extension
                show you is defensible, not decorative.
              </p>
            </div>

            {/* Feature ledger */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-line">
              {[
                {
                  title: "Ownership, not branding",
                  detail:
                    "Verified company-level ownership data. Not the label. Not the marketing.",
                },
                {
                  title: "Redirect at checkout",
                  detail:
                    "Surfaces a verified women-owned alternative the moment your scan or cart choice falls short.",
                },
                {
                  title: "Now covering services",
                  detail:
                    "Move beyond consumer retail. Instantly source and support verified women-led firms in critical, high-impact service sectors.",
                },
                {
                  title: "Defensible data",
                  detail:
                    "Proprietary metadata architecture. Continually updated, rigorously vetted, and integrated across mobile and desktop.",
                },
              ].map((f, i) => (
                <div
                  key={i}
                  data-testid={`feature-${i}`}
                  className="py-6 border-b border-line sm:odd:border-r sm:odd:pr-8 sm:even:pl-8"
                >
                  <div className="flex items-center gap-2 text-brand-orange mb-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
                      0{i + 1}
                    </span>
                  </div>
                  <h4 className="font-display font-bold text-ink text-lg">
                    {f.title}
                  </h4>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed">
                    {f.detail}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Scan demo card */}
          <div className="lg:col-span-5 lg:border-l lg:border-line lg:pl-10">
            <div className="border border-line bg-ink">
              <div className="flex items-center justify-between px-5 py-3 border-b border-line">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                  Live scan · demo
                </span>
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-brand-orange" />
                  <span className="w-2 h-2 rounded-full bg-white/30" />
                  <span className="w-2 h-2 rounded-full bg-white/30" />
                </div>
              </div>

              {/* Scan animation */}
              <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-b from-ink via-[#072a1b] to-ink">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 rounded-full bg-brand-red flex items-center justify-center">
                    <ScanLine className="w-14 h-14 text-ink" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="absolute inset-x-8 h-[2px] bg-brand-orange shadow-[0_0_24px_4px_rgba(230,81,0,0.5)] animate-scan-line" />
                {/* Grid overlay */}
                <div className="absolute inset-0 opacity-[0.06] grid grid-cols-8">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="border-l border-line" />
                  ))}
                </div>
              </div>

              {/* Result readout */}
              <div className="px-5 py-5 border-t border-line space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                    Scanned
                  </span>
                  <span className="font-mono text-xs text-ink">0 7 8 4 2 9 3 1 0 5</span>
                </div>
                <div className="flex items-center justify-between pb-4 border-b border-line">
                  <span className="font-display font-bold text-ink text-sm uppercase">
                    Not women-owned
                  </span>
                  <span className="text-brand-red font-mono text-xs uppercase tracking-[0.2em]">
                    ✕ fail
                  </span>
                </div>
                <div className="flex items-center justify-between text-brand-orange">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
                    Alternative surfaced
                  </span>
                  <ArrowUpRight className="w-4 h-4" />
                </div>
                <div className="pt-1">
                  <div className="font-display font-bold text-ink text-lg">
                    Verified women-owned match
                  </div>
                  <p className="text-sm text-ink-soft mt-1">
                    1.9s to re-route the dollar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
